import lodash from 'lodash'
import React, { createRef, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import Button from '../../../../../1stlog-ui/src/components/Button'
import { useField } from '../../../../../1stlog-ui/src/components/Field'
import Popup from '../../../../../1stlog-ui/src/components/Popup'
import { usePrevious } from '../../../../../1stlog-ui/src/hooks'
import { relevantAddressProps } from '../../utils'

const Input = styled.input`
  font-size: 1.1em;
  font-weight: bold;
  text-align: center;
`

export type TProps = {
  addresses: (object | undefined)[]
  onKeyUp?: (e) => void
}

const contactProps = [
  'phone1',
  'mail',
  'eori',
  'ustid',
  'customerAddressHint',
  'showContact',
]

const AddressSwitch = (props: TProps): React.ReactElement => {
  const { addresses = [] } = props

  const refInput = createRef<HTMLInputElement>()

  const findAddressIndex = (address): number => {
    const index = lodash.findIndex(addresses, (a) =>
      lodash.isEqual(
        lodash.pick(a, lodash.without(relevantAddressProps, ...contactProps)),
        lodash.pick(
          address,
          lodash.without(relevantAddressProps, ...contactProps),
        ),
      ),
    )
    return index
  }

  const [value, onChange] = useField('address', { asState: true })

  // To allow email editing even if address is not editable (for label emailing)
  const valueWithoutEmail = useMemo(() => {
    return lodash.omit(value, ['mail'])
  }, [value])

  const count = lodash.size(addresses)

  const [currentIndex, setCurrentIndex] = useState(0)
  const [showTooltip, setShowTooltip] = useState(false)

  useEffect(() => {
    if (!valueWithoutEmail || String(valueWithoutEmail) === '') return
    const initialIndex = findAddressIndex(valueWithoutEmail)
    setCurrentIndex(initialIndex)
  }, [valueWithoutEmail])

  const increment = (): void => {
    setCurrentIndex(currentIndex < count - 1 ? currentIndex + 1 : 0)
  }

  const decrement = (): void => {
    setCurrentIndex(currentIndex > 0 ? currentIndex - 1 : count - 1)
  }

  const updateIndex = (index): void => {
    // nothing, change with buttons/arrow keys
  }

  // change address when index is changed
  const previousCurrentIndex = usePrevious(currentIndex)
  useEffect(() => {
    if (
      previousCurrentIndex !== undefined &&
      previousCurrentIndex !== currentIndex
    ) {
      onChange(lodash.nth(addresses, currentIndex))
    }
  }, [currentIndex])

  const onKeyUp = (e): void => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
      increment()
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
      decrement()
    } else {
      if (lodash.isFunction(props.onKeyUp)) {
        props.onKeyUp(e)
      }
    }
  }

  const onFocus = (): void => setShowTooltip(true)

  const onBlur = (): void => setShowTooltip(false)

  return (
    <div className="text-center mb-2" style={{ display: 'inline-block' }}>
      <label>
        {i18n.t('multiple_addresses_available', 'Mehrere Adressen verfügbar')}:
      </label>
      <div style={{ width: '150px' }}>
        <div className="input-group">
          <div className="input-group-prepend">
            <Button icon="fas fa-chevron-left" onClick={decrement} />
          </div>
          <Popup placement="top" context={refInput} open={showTooltip} inverted>
            {i18n.t(
              'change_address_via_arrow_keys',
              'Adresse über die Pfeiltasten wechseln',
            )}
          </Popup>
          <Input
            ref={refInput}
            onFocus={onFocus}
            onBlur={onBlur}
            // id="tab_jump_address_switch"
            className="form-control"
            onChange={updateIndex}
            onKeyUp={onKeyUp}
            value={`${currentIndex + 1}/${count}`}
          />
          <div className="input-group-append">
            <Button icon="fas fa-chevron-right" onClick={increment} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddressSwitch
